import type { SupabaseClient } from "@supabase/supabase-js";

// Field Inbox Messages v1 host contract (migrations 20260822150000 and 20260822151000).
// Direct and Crew are first-party RPC surfaces. They are intentionally separate from Channels
// Light, whose channel_messages rows remain announcement-only Boards.

export const LIST_MESSAGE_THREADS_RPC = "list_message_threads";
export const GET_MESSAGE_THREAD_RPC = "get_message_thread";
export const LIST_MESSAGE_POSTS_RPC = "list_message_posts";
export const SEARCH_MESSAGE_START_ROSTER_RPC = "search_message_start_roster";
export const CREATE_MESSAGE_DIRECT_RPC = "create_message_direct";
export const CREATE_MESSAGE_CREW_RPC = "create_message_crew";
export const SEND_MESSAGE_POST_RPC = "send_message_post";
export const MARK_MESSAGE_POST_DELIVERED_RPC = "mark_message_post_delivered";
export const MARK_MESSAGE_POST_SEEN_RPC = "mark_message_post_seen";

export const MESSAGE_THREAD_LIMIT = 100;
export const MESSAGE_POST_PAGE_LIMIT = 50;
export const MESSAGE_ROSTER_LIMIT = 50;

export const MESSAGE_KINDS = ["direct", "crew"] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export interface MessageThread {
  readonly threadId: string;
  readonly kind: MessageKind;
  readonly name: string;
  readonly siteId: string | null;
  readonly shiftTemplateId: string | null;
  readonly memberCount: number;
  readonly unreadPostCount: number;
  readonly lastPostId: string | null;
  readonly lastPostBody: string | null;
  readonly lastPostAuthorDisplayName: string | null;
  readonly lastPostAt: string | null;
  readonly callerCanSend: boolean;
  readonly createdAt: string;
}

export interface MessageThreadMember {
  readonly membershipId: string;
  readonly displayName: string;
  readonly role: string;
  readonly kind: string;
  readonly joinedAt: string;
}

export interface MessageThreadDetail {
  readonly thread: Omit<MessageThread, "lastPostId" | "lastPostBody" | "lastPostAuthorDisplayName" | "lastPostAt" | "unreadPostCount">;
  readonly members: readonly MessageThreadMember[];
}

export interface MessagePost {
  readonly postId: string;
  readonly threadId: string;
  readonly body: string;
  readonly authorMembershipId: string;
  readonly authorDisplayName: string;
  readonly authorRole: string;
  readonly isOwnPost: boolean;
  readonly recipientCount: number;
  readonly deliveredCount: number;
  readonly seenCount: number;
  readonly createdAt: string;
}

export interface MessageRosterEntry {
  readonly membershipId: string;
  readonly displayName: string;
  readonly role: string;
  readonly reachKind: "same_site" | "dispatch" | "site_roster" | "shift_roster";
}

export interface CreatedMessageThread {
  readonly threadId: string;
  readonly kind: MessageKind;
  readonly name: string;
  readonly siteId: string | null;
  readonly shiftTemplateId: string | null;
  readonly memberCount: number;
  readonly createdAt: string;
  readonly wasCreated: boolean;
}

export interface SentMessagePost {
  readonly postId: string;
  readonly threadId: string;
  readonly body: string;
  readonly authorMembershipId: string;
  readonly authorDisplayName: string;
  readonly authorRole: string;
  readonly recipientCount: number;
  readonly wasDuplicate: boolean;
  readonly createdAt: string;
}

export type MessageLoad<T> =
  | { readonly status: "ready"; readonly value: T }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export type MessageFailureKind =
  | "auth"
  | "invalid"
  | "membership"
  | "rate"
  | "reach"
  | "unavailable"
  | "unknown";

export type MessageWrite<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly kind: MessageFailureKind; readonly reason: string };

export const MESSAGES_UNAVAILABLE =
  "Messages are not available on this server yet. Nothing was sent or missed.";
export const MESSAGE_THREADS_LOAD_FAILED =
  "Could not load Messages. Check your connection and try again.";
export const MESSAGE_THREAD_LOAD_FAILED =
  "Could not load this thread. Check your connection and try again.";
export const MESSAGE_THREAD_MEMBERSHIP_UNAVAILABLE =
  "This thread is no longer available to your membership.";
export const MESSAGE_ROSTER_LOAD_FAILED =
  "Could not load the eligible roster. Check your connection and try again.";
export const MESSAGE_ACTION_FAILED = "Could not complete that Messages action. Try again.";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MISSING_SURFACE_CODES: ReadonlySet<string> = new Set(["42883", "42P01", "PGRST202", "PGRST205"]);

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function nullableText(value: unknown): string | null {
  return value == null ? null : text(value);
}

function uuid(value: unknown): string | null {
  const parsed = text(value);
  return parsed !== null && UUID_PATTERN.test(parsed) ? parsed : null;
}

function nullableUuid(value: unknown): string | null {
  return value == null ? null : uuid(value);
}

function iso(value: unknown): string | null {
  const parsed = text(value);
  return parsed !== null && Number.isFinite(Date.parse(parsed)) ? parsed : null;
}

function count(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function kind(value: unknown): MessageKind | null {
  return typeof value === "string" && (MESSAGE_KINDS as readonly string[]).includes(value)
    ? (value as MessageKind)
    : null;
}

function rows(value: unknown): readonly unknown[] | null {
  if (value == null) return [];
  return Array.isArray(value) ? value : null;
}

function oneRow(value: unknown): Record<string, unknown> | null {
  const list = rows(value);
  return list !== null && list.length === 1 ? record(list[0]) : null;
}

export function mapMessageThread(raw: unknown): MessageThread | null {
  const row = record(raw);
  if (row === null) return null;
  const threadId = uuid(row.thread_id);
  const threadKind = kind(row.thread_kind);
  const name = text(row.thread_name);
  const memberCount = count(row.member_count);
  const unreadPostCount = count(row.unread_post_count);
  const createdAt = iso(row.created_at);
  const siteId = nullableUuid(row.site_id);
  const shiftTemplateId = nullableUuid(row.shift_template_id);
  const lastPostId = nullableUuid(row.last_post_id);
  const lastPostBody = nullableText(row.last_post_body);
  const lastPostAuthorDisplayName = nullableText(row.last_post_author_display_name);
  const lastPostAt = row.last_post_at == null ? null : iso(row.last_post_at);
  if (
    threadId === null ||
    threadKind === null ||
    name === null ||
    memberCount === null ||
    unreadPostCount === null ||
    createdAt === null ||
    typeof row.caller_can_send !== "boolean" ||
    (row.site_id != null && siteId === null) ||
    (row.shift_template_id != null && shiftTemplateId === null) ||
    (row.last_post_id != null && lastPostId === null) ||
    (row.last_post_at != null && lastPostAt === null) ||
    (threadKind === "direct"
      ? memberCount !== 2 || siteId !== null || shiftTemplateId !== null
      : memberCount < 2 || memberCount > 25 || (siteId === null) === (shiftTemplateId === null)) ||
    (lastPostId === null
      ? lastPostBody !== null || lastPostAuthorDisplayName !== null || lastPostAt !== null
      : lastPostBody === null || lastPostAuthorDisplayName === null || lastPostAt === null)
  ) {
    return null;
  }
  return {
    threadId,
    kind: threadKind,
    name,
    siteId,
    shiftTemplateId,
    memberCount,
    unreadPostCount,
    lastPostId,
    lastPostBody,
    lastPostAuthorDisplayName,
    lastPostAt,
    callerCanSend: row.caller_can_send,
    createdAt,
  };
}

export function mapMessageThreads(raw: unknown): MessageThread[] | null {
  const list = rows(raw);
  if (list === null) return null;
  const mapped = list.map(mapMessageThread);
  return mapped.every((item): item is MessageThread => item !== null) ? mapped : null;
}

export function mapMessageThreadDetail(raw: unknown): MessageThreadDetail | null {
  const list = rows(raw);
  if (list === null || list.length === 0) return null;
  const first = record(list[0]);
  if (first === null) return null;
  const threadId = uuid(first.thread_id);
  const threadKind = kind(first.thread_kind);
  const name = text(first.thread_name);
  const siteId = nullableUuid(first.site_id);
  const shiftTemplateId = nullableUuid(first.shift_template_id);
  const memberCount = count(first.member_count);
  const createdAt = iso(first.created_at);
  if (
    threadId === null ||
    threadKind === null ||
    name === null ||
    memberCount === null ||
    createdAt === null ||
    typeof first.caller_can_send !== "boolean" ||
    (first.site_id != null && siteId === null) ||
    (first.shift_template_id != null && shiftTemplateId === null) ||
    (threadKind === "direct"
      ? memberCount !== 2 || siteId !== null || shiftTemplateId !== null
      : memberCount < 2 || memberCount > 25 || (siteId === null) === (shiftTemplateId === null))
  ) {
    return null;
  }
  const members: MessageThreadMember[] = [];
  const memberIds = new Set<string>();
  for (const item of list) {
    const row = record(item);
    const rowSiteId = row === null ? null : nullableUuid(row.site_id);
    const rowShiftTemplateId = row === null ? null : nullableUuid(row.shift_template_id);
    if (
      row === null ||
      uuid(row.thread_id) !== threadId ||
      kind(row.thread_kind) !== threadKind ||
      text(row.thread_name) !== name ||
      (row.site_id != null && rowSiteId === null) ||
      (row.shift_template_id != null && rowShiftTemplateId === null) ||
      rowSiteId !== siteId ||
      rowShiftTemplateId !== shiftTemplateId ||
      count(row.member_count) !== memberCount ||
      iso(row.created_at) !== createdAt ||
      row.caller_can_send !== first.caller_can_send
    ) {
      return null;
    }
    const membershipId = uuid(row.member_membership_id);
    const displayName = text(row.member_display_name);
    const role = text(row.member_role);
    const memberKind = text(row.member_kind);
    const joinedAt = iso(row.member_joined_at);
    if (
      membershipId === null ||
      displayName === null ||
      role === null ||
      memberKind === null ||
      joinedAt === null
    ) {
      return null;
    }
    if (memberIds.has(membershipId)) return null;
    memberIds.add(membershipId);
    members.push({ membershipId, displayName, role, kind: memberKind, joinedAt });
  }
  if (members.length !== memberCount) return null;
  return {
    thread: {
      threadId,
      kind: threadKind,
      name,
      siteId,
      shiftTemplateId,
      memberCount,
      callerCanSend: first.caller_can_send,
      createdAt,
    },
    members,
  };
}

export function mapMessagePost(raw: unknown): MessagePost | null {
  const row = record(raw);
  if (row === null) return null;
  const postId = uuid(row.post_id);
  const threadId = uuid(row.thread_id);
  const body = text(row.body);
  const authorMembershipId = uuid(row.author_membership_id);
  const authorDisplayName = text(row.author_display_name);
  const authorRole = text(row.author_role);
  const recipientCount = count(row.recipient_count);
  const deliveredCount = count(row.delivered_count);
  const seenCount = count(row.seen_count);
  const createdAt = iso(row.created_at);
  if (
    postId === null ||
    threadId === null ||
    body === null ||
    authorMembershipId === null ||
    authorDisplayName === null ||
    authorRole === null ||
    recipientCount === null ||
    deliveredCount === null ||
    seenCount === null ||
    createdAt === null ||
    typeof row.is_own_post !== "boolean" ||
    recipientCount === 0 ||
    deliveredCount > recipientCount ||
    seenCount > deliveredCount
  ) {
    return null;
  }
  return {
    postId,
    threadId,
    body,
    authorMembershipId,
    authorDisplayName,
    authorRole,
    isOwnPost: row.is_own_post,
    recipientCount,
    deliveredCount,
    seenCount,
    createdAt,
  };
}

export function mapMessagePosts(raw: unknown): MessagePost[] | null {
  const list = rows(raw);
  if (list === null) return null;
  const mapped = list.map(mapMessagePost);
  return mapped.every((item): item is MessagePost => item !== null) ? mapped : null;
}

export function mapMessageRoster(raw: unknown): MessageRosterEntry[] | null {
  const list = rows(raw);
  if (list === null) return null;
  const result: MessageRosterEntry[] = [];
  for (const item of list) {
    const row = record(item);
    const membershipId = row === null ? null : uuid(row.membership_id);
    const displayName = row === null ? null : text(row.display_name);
    const role = row === null ? null : text(row.role);
    const reachKind = row === null ? null : text(row.reach_kind);
    if (
      membershipId === null ||
      displayName === null ||
      role === null ||
      !["same_site", "dispatch", "site_roster", "shift_roster"].includes(reachKind ?? "")
    ) {
      return null;
    }
    result.push({
      membershipId,
      displayName,
      role,
      reachKind: reachKind as MessageRosterEntry["reachKind"],
    });
  }
  return result;
}

export function messageReceiptLabel(post: Pick<MessagePost, "deliveredCount" | "recipientCount" | "seenCount">): string {
  if (post.seenCount > 0) return `Seen by ${post.seenCount} of ${post.recipientCount}`;
  if (post.deliveredCount > 0) return `Delivered to ${post.deliveredCount} of ${post.recipientCount}`;
  return "Sent";
}

export function messageReceiptLine(
  post: Pick<MessagePost, "createdAt" | "deliveredCount" | "recipientCount" | "seenCount">,
  formatWhen: (iso: string) => string,
): string {
  const when = formatWhen(post.createdAt);
  return when === "" ? messageReceiptLabel(post) : `${messageReceiptLabel(post)} · ${when}`;
}

export function isMissingMessageSurface(error: unknown): boolean {
  const row = record(error);
  const code = row === null ? null : text(row.code);
  if (code !== null && MISSING_SURFACE_CODES.has(code)) return true;
  const message = row === null ? null : text(row.message);
  if (message === null) return false;
  const lower = message.toLowerCase();
  return lower.includes("could not find the function") ||
    (/function\s+[^\s]*message/.test(lower) && lower.includes("does not exist")) ||
    (/relation\s+[^\s]*message_/.test(lower) && lower.includes("does not exist"));
}

export function mapMessageFailure(error: unknown): { readonly kind: MessageFailureKind; readonly reason: string } {
  if (isMissingMessageSurface(error)) return { kind: "unavailable", reason: MESSAGES_UNAVAILABLE };
  const row = record(error);
  const message = row === null ? "" : (text(row.message) ?? "");
  if (/authentication is required|caller mismatch/i.test(message)) {
    return { kind: "auth", reason: "Your Messages session ended. Sign in again." };
  }
  if (/active account membership|active field membership/i.test(message)) {
    return { kind: "membership", reason: "An active field membership is required for Messages." };
  }
  if (/direct recipient|current roster|choose another account member/i.test(message)) {
    return {
      kind: "reach",
      reason: "That officer is not on your current roster. Refresh and choose someone available.",
    };
  }
  if (/crew members|crew site roster|crew shift roster|requested crew roster|caller is not on/i.test(message)) {
    return {
      kind: "reach",
      reason: "That crew roster changed. Refresh the roster and try again.",
    };
  }
  if (/thread was not found|post was not found|not available/i.test(message)) {
    return { kind: "membership", reason: "This thread is no longer available to your membership." };
  }
  if (/rate limit exceeded/i.test(message)) {
    return { kind: "rate", reason: "Messages are busy. Wait a moment and try again." };
  }
  if (/must be between|required|must be direct or crew|does not take a crew target/i.test(message)) {
    return { kind: "invalid", reason: MESSAGE_ACTION_FAILED };
  }
  return { kind: "unknown", reason: MESSAGE_ACTION_FAILED };
}

function loadFailure<T>(error: unknown, fallback: string): MessageLoad<T> {
  return isMissingMessageSurface(error)
    ? { status: "unavailable", reason: MESSAGES_UNAVAILABLE }
    : { status: "failed", reason: fallback };
}

function threadLoadFailure<T>(error: unknown): MessageLoad<T> {
  if (isMissingMessageSurface(error)) {
    return { status: "unavailable", reason: MESSAGES_UNAVAILABLE };
  }
  const mapped = mapMessageFailure(error);
  return mapped.kind === "membership"
    ? { status: "failed", reason: MESSAGE_THREAD_MEMBERSHIP_UNAVAILABLE }
    : { status: "failed", reason: MESSAGE_THREAD_LOAD_FAILED };
}

export async function fetchMessageThreads(
  client: SupabaseClient,
  accountId: string,
): Promise<MessageLoad<readonly MessageThread[]>> {
  try {
    const { data, error } = await client.rpc(LIST_MESSAGE_THREADS_RPC, {
      p_account_id: accountId,
      p_limit: MESSAGE_THREAD_LIMIT,
    });
    if (error) return loadFailure(error, MESSAGE_THREADS_LOAD_FAILED);
    const mapped = mapMessageThreads(data);
    return mapped === null
      ? { status: "failed", reason: MESSAGE_THREADS_LOAD_FAILED }
      : { status: "ready", value: mapped };
  } catch (error) {
    return loadFailure(error, MESSAGE_THREADS_LOAD_FAILED);
  }
}

export async function fetchMessageThread(
  client: SupabaseClient,
  accountId: string,
  threadId: string,
): Promise<MessageLoad<MessageThreadDetail>> {
  try {
    const { data, error } = await client.rpc(GET_MESSAGE_THREAD_RPC, {
      p_account_id: accountId,
      p_thread_id: threadId,
    });
    if (error) return threadLoadFailure(error);
    const mapped = mapMessageThreadDetail(data);
    return mapped === null || mapped.thread.threadId !== threadId
      ? { status: "failed", reason: MESSAGE_THREAD_LOAD_FAILED }
      : { status: "ready", value: mapped };
  } catch (error) {
    return threadLoadFailure(error);
  }
}

export async function fetchMessagePosts(
  client: SupabaseClient,
  accountId: string,
  threadId: string,
  beforePostId: string | null = null,
): Promise<MessageLoad<readonly MessagePost[]>> {
  try {
    const { data, error } = await client.rpc(LIST_MESSAGE_POSTS_RPC, {
      p_account_id: accountId,
      p_thread_id: threadId,
      p_limit: MESSAGE_POST_PAGE_LIMIT,
      p_before_post_id: beforePostId,
    });
    if (error) return threadLoadFailure(error);
    const mapped = mapMessagePosts(data);
    return mapped === null || mapped.some((post) => post.threadId !== threadId)
      ? { status: "failed", reason: MESSAGE_THREAD_LOAD_FAILED }
      : { status: "ready", value: mapped };
  } catch (error) {
    return threadLoadFailure(error);
  }
}

export async function searchMessageRoster(
  client: SupabaseClient,
  input: {
    readonly accountId: string;
    readonly kind: MessageKind;
    readonly query: string;
    readonly siteId?: string | null;
    readonly shiftTemplateId?: string | null;
  },
): Promise<MessageLoad<readonly MessageRosterEntry[]>> {
  try {
    const { data, error } = await client.rpc(SEARCH_MESSAGE_START_ROSTER_RPC, {
      p_account_id: input.accountId,
      p_kind: input.kind,
      p_query: input.query.trim(),
      p_site_id: input.siteId ?? null,
      p_shift_template_id: input.shiftTemplateId ?? null,
      p_limit: MESSAGE_ROSTER_LIMIT,
    });
    if (error) return loadFailure(error, MESSAGE_ROSTER_LOAD_FAILED);
    const mapped = mapMessageRoster(data);
    return mapped === null
      ? { status: "failed", reason: MESSAGE_ROSTER_LOAD_FAILED }
      : { status: "ready", value: mapped };
  } catch (error) {
    return loadFailure(error, MESSAGE_ROSTER_LOAD_FAILED);
  }
}

function createdDirect(raw: unknown, expectedOtherMembershipId: string): CreatedMessageThread | null {
  const row = oneRow(raw);
  if (row === null) return null;
  const threadId = uuid(row.thread_id);
  const name = text(row.thread_name);
  const otherMembershipId = uuid(row.other_membership_id);
  const otherDisplayName = text(row.other_display_name);
  const createdAt = iso(row.created_at);
  if (
    threadId === null ||
    row.thread_kind !== "direct" ||
    name === null ||
    otherMembershipId !== expectedOtherMembershipId ||
    otherDisplayName === null ||
    name !== otherDisplayName ||
    createdAt === null ||
    typeof row.was_created !== "boolean"
  ) {
    return null;
  }
  return {
    threadId,
    kind: "direct",
    name,
    siteId: null,
    shiftTemplateId: null,
    memberCount: 2,
    createdAt,
    wasCreated: row.was_created,
  };
}

function createdCrew(raw: unknown): CreatedMessageThread | null {
  const row = oneRow(raw);
  if (row === null) return null;
  const threadId = uuid(row.thread_id);
  const name = text(row.thread_name);
  const memberCount = count(row.member_count);
  const createdAt = iso(row.created_at);
  const siteId = nullableUuid(row.site_id);
  const shiftTemplateId = nullableUuid(row.shift_template_id);
  if (
    threadId === null ||
    name === null ||
    row.thread_kind !== "crew" ||
    memberCount === null ||
    memberCount < 2 ||
    memberCount > 25 ||
    createdAt === null ||
    (row.site_id != null && siteId === null) ||
    (row.shift_template_id != null && shiftTemplateId === null) ||
    (siteId === null) === (shiftTemplateId === null)
  ) return null;
  return {
    threadId,
    kind: "crew",
    name,
    siteId,
    shiftTemplateId,
    memberCount,
    createdAt,
    wasCreated: true,
  };
}

async function write<T>(
  run: () => PromiseLike<{ data: unknown; error: unknown }>,
  parse: (raw: unknown) => T | null,
): Promise<MessageWrite<T>> {
  try {
    const { data, error } = await run();
    if (error) return { ok: false, ...mapMessageFailure(error) };
    const value = parse(data);
    return value === null
      ? { ok: false, kind: "unknown", reason: MESSAGE_ACTION_FAILED }
      : { ok: true, value };
  } catch (error) {
    return { ok: false, ...mapMessageFailure(error) };
  }
}

export async function createMessageDirect(
  client: SupabaseClient,
  accountId: string,
  otherMembershipId: string,
): Promise<MessageWrite<CreatedMessageThread>> {
  return write(
    () => client.rpc(CREATE_MESSAGE_DIRECT_RPC, {
      p_account_id: accountId,
      p_other_membership_id: otherMembershipId,
    }),
    (raw) => createdDirect(raw, otherMembershipId),
  );
}

export async function createMessageCrew(
  client: SupabaseClient,
  input: {
    readonly accountId: string;
    readonly name: string;
    readonly memberIds: readonly string[];
    readonly siteId?: string | null;
    readonly shiftTemplateId?: string | null;
  },
): Promise<MessageWrite<CreatedMessageThread>> {
  return write(
    () => client.rpc(CREATE_MESSAGE_CREW_RPC, {
      p_account_id: input.accountId,
      p_name: input.name.trim(),
      p_site_id: input.siteId ?? null,
      p_shift_template_id: input.shiftTemplateId ?? null,
      p_member_ids: [...input.memberIds],
    }),
    createdCrew,
  );
}

function sentPost(raw: unknown): SentMessagePost | null {
  const row = oneRow(raw);
  if (row === null) return null;
  const postId = uuid(row.post_id);
  const threadId = uuid(row.thread_id);
  const body = text(row.body);
  const authorMembershipId = uuid(row.author_membership_id);
  const authorDisplayName = text(row.author_display_name);
  const authorRole = text(row.author_role);
  const recipientCount = count(row.recipient_count);
  const createdAt = iso(row.created_at);
  if (
    postId === null ||
    threadId === null ||
    body === null ||
    authorMembershipId === null ||
    authorDisplayName === null ||
    authorRole === null ||
    recipientCount === null ||
    recipientCount === 0 ||
    createdAt === null ||
    typeof row.was_duplicate !== "boolean"
  ) {
    return null;
  }
  return {
    postId,
    threadId,
    body,
    authorMembershipId,
    authorDisplayName,
    authorRole,
    recipientCount,
    wasDuplicate: row.was_duplicate,
    createdAt,
  };
}

export async function sendMessagePost(
  client: SupabaseClient,
  input: {
    readonly accountId: string;
    readonly threadId: string;
    readonly body: string;
    readonly clientPostId: string;
  },
): Promise<MessageWrite<SentMessagePost>> {
  return write(
    () => client.rpc(SEND_MESSAGE_POST_RPC, {
      p_account_id: input.accountId,
      p_thread_id: input.threadId,
      p_body: input.body.trim(),
      p_client_post_id: input.clientPostId,
    }),
    (raw) => {
      const parsed = sentPost(raw);
      return parsed !== null && parsed.threadId === input.threadId && parsed.body === input.body.trim()
        ? parsed
        : null;
    },
  );
}

function receiptRecorded(
  raw: unknown,
  expectedPostId: string,
  requireSeen: boolean,
): true | null {
  const row = oneRow(raw);
  if (row === null || uuid(row.post_id) !== expectedPostId) return null;
  const deliveredAt = iso(row.delivered_at);
  const seenAt = row.seen_at == null ? null : iso(row.seen_at);
  if (deliveredAt === null || (row.seen_at != null && seenAt === null) || (requireSeen && seenAt === null)) {
    return null;
  }
  return true;
}

async function markMessagePostReceipt(
  client: SupabaseClient,
  rpc: typeof MARK_MESSAGE_POST_DELIVERED_RPC | typeof MARK_MESSAGE_POST_SEEN_RPC,
  accountId: string,
  postId: string,
): Promise<MessageWrite<true>> {
  return write(
    () => client.rpc(rpc, { p_account_id: accountId, p_post_id: postId }),
    (raw) => receiptRecorded(raw, postId, rpc === MARK_MESSAGE_POST_SEEN_RPC),
  );
}

export function markMessagePostDelivered(
  client: SupabaseClient,
  accountId: string,
  postId: string,
): Promise<MessageWrite<true>> {
  return markMessagePostReceipt(client, MARK_MESSAGE_POST_DELIVERED_RPC, accountId, postId);
}

export function markMessagePostSeen(
  client: SupabaseClient,
  accountId: string,
  postId: string,
): Promise<MessageWrite<true>> {
  return markMessagePostReceipt(client, MARK_MESSAGE_POST_SEEN_RPC, accountId, postId);
}
